import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useThemeMode } from '../../ThemeContext';
import { APP_URLS } from '../../helpers/url_helper';
import { dataAPI } from '../../helpers/api_service';
import AppIconBlue from '../../assets/AppIconBlue.svg';
import '../../styles/loading-animations.css';

const initialForm = {
  asset_id: '',
  asset_name: '',
  asset_type: '',
  manufacturer: '',
  model: '',
  serial_number: '',
  rego_number: '',
  rego_expiry: '',
  odometer_km: '',
  engine_hours: '',
  last_service_date: '',
  last_service_km: '',
  last_service_hours: '',
  service_interval_km: '',
  service_interval_hours: '',
  service_interval_months: '',
  next_service_due: '',
  status: '',
  condition: '',
  location: '',
  assigned_to: '',
};

const fieldConfig = [
  { key: 'asset_id', label: 'Asset ID', required: true, placeholder: 'e.g. AST-001' },
  { key: 'asset_name', label: 'Asset Name', required: true, placeholder: 'e.g. Hilux Ute, Excavator' },
  { key: 'asset_type', label: 'Asset Type', required: true, placeholder: 'Vehicle / Plant / Equipment / Tool' },
  { key: 'manufacturer', label: 'Manufacturer', placeholder: 'e.g. Toyota, CAT' },
  { key: 'model', label: 'Model', placeholder: 'Model name or number' },
  { key: 'serial_number', label: 'Serial Number', placeholder: 'Manufacturer serial number' },
  { key: 'rego_number', label: 'Rego Number', placeholder: 'If applicable' },
  { key: 'rego_expiry', label: 'Rego Expiry', type: 'date' },
  { key: 'odometer_km', label: 'Odometer (KM)', type: 'number', placeholder: 'For vehicles' },
  { key: 'engine_hours', label: 'Engine Hours (Hrs)', type: 'number', placeholder: 'For plant' },
  { key: 'last_service_date', label: 'Last Service Date', type: 'date' },
  { key: 'last_service_km', label: 'Last Service KM', type: 'number', placeholder: 'Optional' },
  { key: 'last_service_hours', label: 'Last Service Hours', type: 'number', placeholder: 'Optional' },
  { key: 'service_interval_km', label: 'Service Interval (KM)', type: 'number', placeholder: 'e.g. 10000' },
  { key: 'service_interval_hours', label: 'Service Interval (Hrs)', type: 'number', placeholder: 'e.g. 250' },
  { key: 'service_interval_months', label: 'Service Interval (Months)', type: 'number', placeholder: 'e.g. 6' },
  { key: 'next_service_due', label: 'Next Service Due', type: 'date' },
  {
    key: 'status',
    label: 'Status',
    required: true,
    type: 'select',
    placeholder: 'Select status',
    options: [
      { value: 'active', label: 'Active' },
      { value: 'in_use', label: 'In Use' },
      { value: 'out_of_service', label: 'Out of Service' },
    ],
  },
  {
    key: 'condition',
    label: 'Condition',
    type: 'select',
    placeholder: 'Select condition',
    options: [
      { value: 'Good', label: 'Good' },
      { value: 'Fair', label: 'Fair' },
      { value: 'Poor', label: 'Poor' },
    ],
  },
  { key: 'location', label: 'Location', placeholder: 'Site or job' },
  { key: 'assigned_to', label: 'Assigned To', placeholder: 'Worker or team' },
];

const AssetRecordForm = () => {
  const { mode } = useThemeMode();
  const navigate = useNavigate();
  const { assetId } = useParams();
  const isEditMode = Boolean(assetId);
  const [formData, setFormData] = useState(initialForm);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [isLoadingEmployees, setIsLoadingEmployees] = useState(false);
  const [employeeLoadError, setEmployeeLoadError] = useState('');
  const [isLoadingAsset, setIsLoadingAsset] = useState(false);

  const styles = useMemo(() => ({
    page: {
      padding: '24px',
      minHeight: '100vh',
      background: mode === 'dark' ? '#0f0f0f' : '#ffffff',
      color: mode === 'dark' ? '#ffffff' : '#1f2937',
      fontFamily: 'Urbanist, Arial, sans-serif',
    },
    card: {
      border: `1px solid ${mode === 'dark' ? '#2a2a2a' : '#e5e7eb'}`,
      borderRadius: '14px',
      padding: '20px',
      background: mode === 'dark' ? '#111111' : '#ffffff',
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
      gap: '14px',
    },
    input: {
      width: '100%',
      borderRadius: '8px',
      border: `1px solid ${mode === 'dark' ? '#374151' : '#d1d5db'}`,
      padding: '10px 12px',
      background: mode === 'dark' ? '#0b0b0b' : '#ffffff',
      color: mode === 'dark' ? '#ffffff' : '#111827',
      outline: 'none',
      fontSize: '14px',
      boxSizing: 'border-box',
    },
  }), [mode]);

  const onChange = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  useEffect(() => {
    if (!isEditMode) return;

    const loadAssetForEdit = async () => {
      try {
        setIsLoadingAsset(true);
        setError('');
        const response = await dataAPI.getAssetRecords({ showLoading: false });
        const records = Array.isArray(response?.data) ? response.data : [];
        const matchedAsset = records.find((item) => String(item.id) === String(assetId));

        if (!matchedAsset) {
          setError('Asset record not found.');
          return;
        }

        setFormData({
          asset_id: matchedAsset.asset_id || '',
          asset_name: matchedAsset.asset_name || '',
          asset_type: matchedAsset.asset_type || '',
          manufacturer: matchedAsset.manufacturer || '',
          model: matchedAsset.model || '',
          serial_number: matchedAsset.serial_number || '',
          rego_number: matchedAsset.rego_number || '',
          rego_expiry: matchedAsset.rego_expiry || '',
          odometer_km: matchedAsset.odometer_km ?? '',
          engine_hours: matchedAsset.engine_hours ?? '',
          last_service_date: matchedAsset.last_service_date || '',
          last_service_km: matchedAsset.last_service_km ?? '',
          last_service_hours: matchedAsset.last_service_hours ?? '',
          service_interval_km: matchedAsset.service_interval_km ?? '',
          service_interval_hours: matchedAsset.service_interval_hours ?? '',
          service_interval_months: matchedAsset.service_interval_months ?? '',
          next_service_due: matchedAsset.next_service_due || '',
          status: matchedAsset.status || '',
          condition: matchedAsset.condition || '',
          location: matchedAsset.location || '',
          assigned_to: matchedAsset.assigned_to ?? '',
        });
      } catch (apiError) {
        setError(apiError?.message || 'Unable to load asset details.');
      } finally {
        setIsLoadingAsset(false);
      }
    };

    loadAssetForEdit();
  }, [assetId, isEditMode]);

  useEffect(() => {
    const loadEmployees = async () => {
      try {
        setIsLoadingEmployees(true);
        setEmployeeLoadError('');
        const response = await dataAPI.getAllEmployees({ showLoading: false });
        setEmployees(Array.isArray(response?.data) ? response.data : []);
      } catch (apiError) {
        setEmployees([]);
        setEmployeeLoadError(apiError?.message || 'Unable to load employees.');
      } finally {
        setIsLoadingEmployees(false);
      }
    };

    loadEmployees();
  }, []);

  const numberOrNull = (value) => {
    if (value === '' || value === null || value === undefined) return null;
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (!formData.asset_id.trim() || !formData.asset_name.trim() || !formData.asset_type.trim() || !formData.status.trim()) {
      setError('Please fill all required fields: Asset ID, Asset Name, Asset Type and Status.');
      return;
    }

    const payload = {
      asset_id: formData.asset_id.trim(),
      asset_name: formData.asset_name.trim(),
      asset_type: formData.asset_type.trim(),
      manufacturer: formData.manufacturer.trim(),
      model: formData.model.trim(),
      serial_number: formData.serial_number.trim(),
      rego_number: formData.rego_number.trim(),
      rego_expiry: formData.rego_expiry || null,
      odometer_km: numberOrNull(formData.odometer_km),
      engine_hours: numberOrNull(formData.engine_hours),
      last_service_date: formData.last_service_date || null,
      last_service_km: numberOrNull(formData.last_service_km),
      last_service_hours: numberOrNull(formData.last_service_hours),
      service_interval_km: numberOrNull(formData.service_interval_km),
      service_interval_hours: numberOrNull(formData.service_interval_hours),
      service_interval_months: numberOrNull(formData.service_interval_months),
      next_service_due: formData.next_service_due || null,
      status: formData.status.trim().toLowerCase(),
      condition: formData.condition.trim(),
      location: formData.location.trim(),
      assigned_to: numberOrNull(formData.assigned_to),
    };

    try {
      setIsSubmitting(true);
      const response = isEditMode
        ? await dataAPI.updateAssetRecord(assetId, payload, { showLoading: true })
        : await dataAPI.createAssetRecord(payload, { showLoading: true });
      if (response?.status === 'success') {
        navigate(isEditMode ? `${APP_URLS.ASSET_RECORD}/${assetId}` : APP_URLS.ASSET_RECORD);
        return;
      }
      setError(response?.message || `Failed to ${isEditMode ? 'update' : 'create'} asset record.`);
    } catch (apiError) {
      setError(apiError?.message || `Failed to ${isEditMode ? 'update' : 'create'} asset record.`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={styles.page}>
      <style>
        {`
          .asset-number-input::-webkit-outer-spin-button,
          .asset-number-input::-webkit-inner-spin-button {
            -webkit-appearance: none;
            margin: 0;
          }
          .asset-number-input[type=number] {
            -moz-appearance: textfield;
            appearance: textfield;
          }
        `}
      </style>
      <div style={{ marginBottom: '16px' }}>
        <button
          type="button"
          onClick={() => navigate(APP_URLS.ASSET_RECORD)}
          style={backButtonStyle(mode)}
        >
          <ArrowBackIcon style={{ fontSize: 18 }} />
          Back to List
        </button>
        <h2 style={{ margin: '14px 0 0 0' }}>{isEditMode ? 'Edit Asset Record' : 'Add Asset Record'}</h2>
      </div>

      <form onSubmit={onSubmit} style={styles.card}>
        {isLoadingAsset && (
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '12px' }}>
            <img src={AppIconBlue} alt="Loading" className="pulse-animation-icon" style={{ width: 30, height: 30 }} />
          </div>
        )}
        {error && <div style={{ color: '#dc2626', marginBottom: '12px' }}>{error}</div>}
        {employeeLoadError && <div style={{ color: '#dc2626', marginBottom: '12px' }}>{employeeLoadError}</div>}
        <div style={styles.grid}>
          {fieldConfig.map((field) => (
            <label key={field.key} style={{ display: 'block' }}>
              <div style={{ fontSize: '13px', marginBottom: '6px' }}>
                {field.label} {field.required ? '*' : ''}
              </div>
              {field.key === 'assigned_to' ? (
                <div style={{ position: 'relative' }}>
                  <select
                    value={formData[field.key]}
                    onChange={(e) => onChange(field.key, e.target.value)}
                    style={styles.input}
                    disabled={isLoadingEmployees}
                  >
                    <option value="">Select employee</option>
                    {employees.map((employee) => (
                      <option key={employee.id} value={employee.id}>
                        {employee.full_name || employee.username || employee.email || `Employee ${employee.id}`}
                      </option>
                    ))}
                  </select>
                  {isLoadingEmployees && (
                    <img
                      src={AppIconBlue}
                      alt="Loading"
                      className="pulse-animation-icon"
                      style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', width: 18, height: 18 }}
                    />
                  )}
                </div>
              ) : field.type === 'select' ? (
                <select
                  value={formData[field.key]}
                  onChange={(e) => onChange(field.key, e.target.value)}
                  style={styles.input}
                >
                  <option value="">{field.placeholder || 'Select option'}</option>
                  {(field.options || []).map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type={field.type || 'text'}
                  value={formData[field.key]}
                  onChange={(e) => onChange(field.key, e.target.value)}
                  placeholder={field.placeholder || ''}
                  style={styles.input}
                  className={field.type === 'number' ? 'asset-number-input' : ''}
                />
              )}
            </label>
          ))}
        </div>

        <div style={{ marginTop: '18px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
          <button type="button" onClick={() => navigate(APP_URLS.ASSET_RECORD)} style={{ padding: '10px 16px', borderRadius: '8px', border: '1px solid #d1d5db', background: 'transparent', cursor: 'pointer', color: mode === 'dark' ? '#fff' : '#111' }} disabled={isSubmitting}>
            Cancel
          </button>
          <button type="submit" style={{ padding: '10px 16px', borderRadius: '8px', border: 'none', background: '#009391', color: '#fff', cursor: 'pointer' }} disabled={isSubmitting || isLoadingAsset}>
            {isEditMode ? 'Update Asset' : 'Save Asset'}
          </button>
        </div>
      </form>
    </div>
  );
};

const backButtonStyle = (mode) => ({
  border: `1px solid ${mode === 'dark' ? '#4a4a4a' : '#d0d5dd'}`,
  background: 'transparent',
  color: mode === 'dark' ? '#fff' : '#111',
  borderRadius: 8,
  padding: '7px 12px',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
});

export default AssetRecordForm;
